import React, { useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import Icon from 'components/AppIcon';

const PortfolioBalances = () => {
  const [viewMode, setViewMode] = useState('overview');
  const [hideSmallBalances, setHideSmallBalances] = useState(false);

  const mockBalances = [
    {
      asset: 'BTC',
      name: 'Bitcoin',
      balance: 0.75842,
      usdValue: 32750.25,
      change24h: 2.34,
      allocation: 45.2,
      avgBuyPrice: 41200.00,
      pnl: 1850.75,
      pnlPercent: 5.98
    },
    {
      asset: 'ETH',
      name: 'Ethereum',
      balance: 12.5847,
      usdValue: 33450.80,
      change24h: -1.85,
      allocation: 46.1,
      avgBuyPrice: 2580.00,
      pnl: -1250.40,
      pnlPercent: -3.60
    },
    {
      asset: 'BNB',
      name: 'BNB',
      balance: 15.2547,
      usdValue: 4805.25,
      change24h: 4.12,
      allocation: 6.6,
      avgBuyPrice: 310.00,
      pnl: 75.85,
      pnlPercent: 1.60
    },
    {
      asset: 'USDT',
      name: 'Tether',
      balance: 1250.75,
      usdValue: 1250.75,
      change24h: 0.01,
      allocation: 1.7,
      avgBuyPrice: 1.00,
      pnl: 0.25,
      pnlPercent: 0.02
    },
    {
      asset: 'ADA',
      name: 'Cardano',
      balance: 850.25,
      usdValue: 410.32,
      change24h: -0.95,
      allocation: 0.6,
      avgBuyPrice: 0.52,
      pnl: -32.15,
      pnlPercent: -7.26
    }
  ];

  const totalPortfolioValue = mockBalances.reduce((sum, balance) => sum + balance.usdValue, 0);
  const totalPnL = mockBalances.reduce((sum, balance) => sum + balance.pnl, 0);
  const totalPnLPercent = (totalPnL / (totalPortfolioValue - totalPnL)) * 100;

  const filteredBalances = hideSmallBalances 
    ? mockBalances.filter(balance => balance.usdValue > 10)
    : mockBalances;

  const pieChartData = filteredBalances.map(balance => ({
    name: balance.asset,
    value: balance.allocation,
    usdValue: balance.usdValue,
    color: getAssetColor(balance.asset)
  }));

  const performanceData = filteredBalances.map(balance => ({
    asset: balance.asset,
    pnl: balance.pnl,
    pnlPercent: balance.pnlPercent
  }));

  function getAssetColor(asset) {
    const colors = {
      'BTC': '#F7931A',
      'ETH': '#627EEA',
      'BNB': '#F3BA2F',
      'USDT': '#26A17B',
      'ADA': '#0033AD'
    };
    return colors[asset] || '#64748B';
  }

  const formatPrice = (price) => {
    return price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatBalance = (balance, decimals = 4) => {
    return balance.toFixed(decimals);
  };

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-surface border border-border rounded-trading shadow-trading-modal p-3">
          <p className="font-body-semibold text-sm text-text-primary mb-1">{data.name}</p>
          <p className="font-data text-sm text-text-primary">
            ${formatPrice(data.usdValue)} ({data.value.toFixed(1)}%)
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div className="flex items-center space-x-4">
          <h4 className="font-heading-semibold text-text-primary">
            Portfolio Overview
          </h4>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
            <span className="text-xs text-text-secondary">Live Prices</span>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {/* View Mode Toggle */}
          <div className="flex bg-secondary-100 rounded-trading p-1">
            <button
              onClick={() => setViewMode('overview')}
              className={`px-3 py-1 rounded-trading font-body-medium text-xs transition-all duration-trading-fast ${
                viewMode === 'overview' ?'bg-surface text-text-primary shadow-trading-card' :'text-text-secondary hover:text-text-primary'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setViewMode('performance')}
              className={`px-3 py-1 rounded-trading font-body-medium text-xs transition-all duration-trading-fast ${
                viewMode === 'performance'
                  ? 'bg-surface text-text-primary shadow-trading-card' :'text-text-secondary hover:text-text-primary'
              }`}
            >
              Performance
            </button>
          </div>

          {/* Hide Small Balances Toggle */}
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={hideSmallBalances}
              onChange={(e) => setHideSmallBalances(e.target.checked)}
              className="rounded border-border focus:ring-primary"
            />
            <span className="text-xs text-text-secondary">Hide small balances</span>
          </label>
        </div>
      </div>

      {/* Portfolio Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-secondary-50 rounded-trading p-6">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="DollarSign" size={20} className="text-text-secondary" />
            <span className="text-sm text-text-secondary">Total Portfolio Value</span>
          </div>
          <div className="font-data text-2xl text-text-primary">
            ${formatPrice(totalPortfolioValue)}
          </div>
        </div>

        <div className="bg-secondary-50 rounded-trading p-6">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="TrendingUp" size={20} className={totalPnL >= 0 ? 'text-success' : 'text-error'} />
            <span className="text-sm text-text-secondary">Total P&L</span>
          </div>
          <div className={`font-data text-2xl ${totalPnL >= 0 ? 'text-success' : 'text-error'}`}>
            {totalPnL >= 0 ? '+' : ''}${formatPrice(totalPnL)}
          </div>
          <div className={`font-data text-sm ${totalPnL >= 0 ? 'text-success' : 'text-error'}`}>
            {totalPnLPercent >= 0 ? '+' : ''}{totalPnLPercent.toFixed(2)}%
          </div>
        </div>

        <div className="bg-secondary-50 rounded-trading p-6">
          <div className="flex items-center space-x-2 mb-2">
            <Icon name="PieChart" size={20} className="text-text-secondary" />
            <span className="text-sm text-text-secondary">Assets</span>
          </div>
          <div className="font-data text-2xl text-text-primary">
            {filteredBalances.length}
          </div>
          <div className="text-sm text-text-secondary">
            {mockBalances.length - filteredBalances.length > 0 && 
              `${mockBalances.length - filteredBalances.length} hidden`
            }
          </div>
        </div>
      </div>

      {/* Main Content */}
      {viewMode === 'overview' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Allocation Chart */}
          <div className="bg-surface border border-border rounded-trading shadow-trading-card p-6">
            <h5 className="font-heading-semibold text-text-primary mb-4">Asset Allocation</h5>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4">
              {pieChartData.map((entry) => (
                <div key={entry.name} className="flex items-center space-x-2">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: entry.color }}
                  ></div>
                  <span className="text-xs text-text-secondary">
                    {entry.name} ({entry.value.toFixed(1)}%)
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Balances Table */}
          <div className="bg-surface border border-border rounded-trading shadow-trading-card">
            <div className="p-6 border-b border-border">
              <h5 className="font-heading-semibold text-text-primary">Asset Balances</h5>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-6 font-body-medium text-sm text-text-secondary">Asset</th>
                    <th className="text-right py-3 px-6 font-body-medium text-sm text-text-secondary">Balance</th>
                    <th className="text-right py-3 px-6 font-body-medium text-sm text-text-secondary">USD Value</th>
                    <th className="text-right py-3 px-6 font-body-medium text-sm text-text-secondary">24h Change</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBalances.map((balance) => (
                    <tr key={balance.asset} className="border-b border-border hover:bg-secondary-50 transition-colors duration-trading-fast">
                      <td className="py-3 px-6">
                        <div className="flex items-center space-x-3">
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white font-body-semibold text-xs"
                            style={{ backgroundColor: getAssetColor(balance.asset) }}
                          >
                            {balance.asset.slice(0, 2)}
                          </div>
                          <div>
                            <div className="font-body-semibold text-sm text-text-primary">
                              {balance.asset}
                            </div>
                            <div className="text-xs text-text-secondary">
                              {balance.name}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-6 text-right">
                        <span className="font-data text-sm text-text-primary">
                          {formatBalance(balance.balance)}
                        </span>
                      </td>
                      <td className="py-3 px-6 text-right">
                        <span className="font-data text-sm text-text-primary">
                          ${formatPrice(balance.usdValue)}
                        </span>
                      </td>
                      <td className="py-3 px-6 text-right">
                        <span className={`font-data text-sm ${
                          balance.change24h >= 0 ? 'text-success' : 'text-error'
                        }`}>
                          {balance.change24h >= 0 ? '+' : ''}{balance.change24h.toFixed(2)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        /* Performance View */
        <div className="space-y-6">
          {/* Performance Chart */}
          <div className="bg-surface border border-border rounded-trading shadow-trading-card p-6">
            <h5 className="font-heading-semibold text-text-primary mb-4">P&L Performance</h5>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={performanceData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="asset" stroke="var(--color-text-secondary)" fontSize={12} />
                  <YAxis stroke="var(--color-text-secondary)" fontSize={12} />
                  <Tooltip 
                    contentStyle={{
                      backgroundColor: 'var(--color-surface)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar 
                    dataKey="pnl" 
                    fill={(entry) => entry.pnl >= 0 ? 'var(--color-success)' : 'var(--color-error)'}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Performance Table */}
          <div className="bg-surface border border-border rounded-trading shadow-trading-card">
            <div className="p-6 border-b border-border">
              <h5 className="font-heading-semibold text-text-primary">Detailed Performance</h5>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left py-3 px-6 font-body-medium text-sm text-text-secondary">Asset</th>
                    <th className="text-right py-3 px-6 font-body-medium text-sm text-text-secondary">Avg Buy Price</th>
                    <th className="text-right py-3 px-6 font-body-medium text-sm text-text-secondary">Current Price</th>
                    <th className="text-right py-3 px-6 font-body-medium text-sm text-text-secondary">P&L</th>
                    <th className="text-right py-3 px-6 font-body-medium text-sm text-text-secondary">P&L %</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBalances.map((balance) => (
                    <tr key={balance.asset} className="border-b border-border hover:bg-secondary-50 transition-colors duration-trading-fast">
                      <td className="py-3 px-6">
                        <div className="flex items-center space-x-3">
                          <div 
                            className="w-8 h-8 rounded-full flex items-center justify-center text-white font-body-semibold text-xs"
                            style={{ backgroundColor: getAssetColor(balance.asset) }}
                          >
                            {balance.asset.slice(0, 2)}
                          </div>
                          <span className="font-body-semibold text-sm text-text-primary">
                            {balance.asset}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-6 text-right">
                        <span className="font-data text-sm text-text-primary">
                          ${formatPrice(balance.avgBuyPrice)}
                        </span>
                      </td>
                      <td className="py-3 px-6 text-right">
                        <span className="font-data text-sm text-text-primary">
                          ${formatPrice(balance.usdValue / balance.balance)}
                        </span>
                      </td>
                      <td className="py-3 px-6 text-right">
                        <span className={`font-data text-sm ${
                          balance.pnl >= 0 ? 'text-success' : 'text-error'
                        }`}>
                          {balance.pnl >= 0 ? '+' : ''}${formatPrice(balance.pnl)}
                        </span>
                      </td>
                      <td className="py-3 px-6 text-right">
                        <span className={`font-data text-sm ${
                          balance.pnlPercent >= 0 ? 'text-success' : 'text-error'
                        }`}>
                          {balance.pnlPercent >= 0 ? '+' : ''}{balance.pnlPercent.toFixed(2)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioBalances;